import React from 'react';
import styled from 'styled-components';

import { Box, Icons, Text, Theme } from 'baukasten';

import { Toast } from './types';

type Color = Parameters<typeof Theme.getColor>[0];
const toastColors: {
  [key in Toast['type']]: { bg: Color; fg: Color };
} = {
  success: { bg: 'green-light', fg: 'green-dark' },
  error: { bg: 'red-light', fg: 'red-dark' },
  warning: { bg: 'yellow-light', fg: 'yellow-dark' },
  info: { bg: 'blue-light', fg: 'blue-dark' },
};

const ToastBox = styled(Box).attrs({
  round: true,
  p: 2.75,
  elevation: 3,
  display: 'flex',
  flexGap: Theme.getBaseUnit(4),
  alignItems: 'center',
  width: Theme.getBaseUnit(108),
  className: 'toast',
})<{ toast: Toast }>`
  position: relative;
  background: ${({ toast }) => Theme.getColor(toastColors[toast.type].bg)};
  border: 1px solid ${({ toast }) => Theme.getColor(toastColors[toast.type].fg)};
`;

const IconBox = styled(Box).attrs({
  height: Theme.getBaseUnit(6),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})``;

const ClickableIconBox = styled(IconBox)`
  cursor: pointer;
  transition: 0.2s ease-in-out;

  &:hover {
    transform: scale(1.2);
  }
`;

const ProgressBar = styled.div<{ duration: number; color: Color }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: ${({ color }) => Theme.getColor(color)};
  animation: shrink ${({ duration }) => duration}s linear forwards;

  @keyframes shrink {
    from {
      width: 100%;
    }
    to {
      width: 0%;
    }
  }
`;

export const ToastNotification = ({
  toast,
  clear,
}: {
  toast: Toast;
  clear: () => void;
}) => {
  return (
    <ToastBox toast={toast}>
      <IconBox>
        <Icons.InfoSolid
          color={toastColors[toast.type].fg}
          noPadding
          size={Theme.getBaseUnit(4)}
        />
      </IconBox>
      <Box width="100%">
        <Text as="span">{toast.message}</Text>
      </Box>
      <ClickableIconBox onClick={clear}>
        <Icons.Close noPadding size={Theme.getBaseUnit(4)} />
      </ClickableIconBox>
      {toast.dismissAfterSeconds > 0 && (
        <ProgressBar
          duration={toast.dismissAfterSeconds}
          color={toastColors[toast.type].fg}
        />
      )}
    </ToastBox>
  );
};
